import {useBus} from "@/core/store/bus";
import {useScopeQuery} from '@/core/store/scopeQuery';
import {useUserStore} from '@/modules/user/store/user';
import {NavigationGuardNext, RouteLocationNormalized} from 'vue-router';

/**
 * Показывает страницу отказа в доступе и открывает окно авторизации
 */
function showAccessDenied(
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
    error: string,
    redirectPath?: string
): void {
    const targetPath = redirectPath || '/access-denied';

    const {bus} = useBus()

    console.log('[authGuard] 🚫 Access denied, redirecting', {
        path: to.path,
        targetPath,
        error,
        isCustomRedirect: !!redirectPath
    });

    // 1. Переадресуем основной роутер на страницу отказа
    next({
        path: targetPath,
        query: {
            returnUrl: to.fullPath,
            error
        }
    });

    // 2. Открываем окно авторизации через stack router поверх страницы с задержкой 1 секунда
    if (!process.env.SERVER) { // ✅ Только на клиенте
        setTimeout(() => {
            bus.emit('router:push', '/auth')
        }, 1000);
    }
}

/**
 * Проверяет авторизацию и роли пользователя для доступа к роуту
 *
 * Логика:
 * 1. На сервере (SSR) - всегда разрешаем переход (возвращаем false)
 * 2. На клиенте:
 *    - Если meta.auth не указан - разрешаем переход (возвращаем false)
 *    - Загружаем scope 'user' если еще не загружен
 *    - Проверяем авторизацию и роли
 *    - При отсутствии доступа показываем /auth или кастомный путь через stack route и вызываем next(false)
 *
 * @param to - целевой роут
 * @param from - исходный роут
 * @param next - функция для продолжения навигации
 * @param collectedMeta - опциональный собранный meta из всех родительских роутов
 * @returns true если guard обработал навигацию (запретил доступ), false если нужно продолжить обработку
 */
export async function authGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
    collectedMeta?: { auth?: boolean | string | string[]; authRedirect?: string }
): Promise<boolean> {
    // Получаем meta.auth и meta.authRedirect из переданного collectedMeta или из to.meta
    const authRequirement = collectedMeta?.auth ?? to.meta.auth;
    const authRedirect = (collectedMeta?.authRedirect ?? to.meta.authRedirect) as string | undefined;

    console.log('[authGuard] 🔍 Checking access:', {
        path: to.path,
        from: from.path,
        isServer: process.env.SERVER,
        authRequirement,
        authRedirect,
        source: collectedMeta?.auth !== undefined ? 'collectedMeta' : 'to.meta'
    });

    // На сервере всегда разрешаем переход
    if (process.env.SERVER) {
        console.log('[authGuard] ⏭️  Skipping on server (SSR)');
        return false;
    }

    // Если auth не указан, разрешаем переход
    if (!authRequirement) {
        console.log('[authGuard] ✅ No auth requirement, access granted');
        return false;
    }

    console.log('[authGuard] 🔐 Auth requirement detected:', {
        type: typeof authRequirement,
        value: authRequirement,
        isBoolean: authRequirement === true,
        isString: typeof authRequirement === 'string',
        isArray: Array.isArray(authRequirement)
    });

    // Загружаем scope 'user' если еще не загружен
    const scopeQuery = useScopeQuery();
    const {fetchedScopes} = scopeQuery;

    console.log('[authGuard] 📊 Scope status:', {
        userScopeFetched: fetchedScopes.user,
        allFetchedScopes: Object.keys(fetchedScopes).filter(key => fetchedScopes[key as keyof typeof fetchedScopes])
    });

    if (!fetchedScopes.user) {
        console.log('[authGuard] 📥 Fetching user scope...');
        try {
            await scopeQuery.fetchUserScopes()
            console.log('[authGuard] ✅ User scope fetched successfully');
        } catch (error) {
            console.error('[authGuard] ❌ Failed to fetch user scope:', error);
            // Продолжаем проверку даже если не удалось загрузить
        }
    }

    // Получаем данные пользователя
    const userStore = useUserStore();
    const {isAuthorized, user} = userStore;

    console.log('[authGuard] 👤 User status:', {
        isAuthorized,
        hasUser: !!user,
        userId: user?.ID,
        userName: user?.NAME,
        userRole: user?.ROLES,
        userKeys: user ? Object.keys(user) : []
    });

    // Проверяем авторизацию
    if (!isAuthorized) {
        console.log('[authGuard] 🚫 User not authorized');
        showAccessDenied(to, next, 'unauthorized', authRedirect);
        return true;
    }

    console.log('[authGuard] ✅ User is authorized');

    // Если auth === true, достаточно быть авторизованным
    if (authRequirement === true) {
        console.log('[authGuard] ✅ Auth requirement is boolean true, access granted');
        return false;
    }

    // Проверяем роли
    const requiredRoles = Array.isArray(authRequirement) ? authRequirement : [authRequirement];
    const userRole = user?.ROLES || null;

    console.log('[authGuard] 🎭 Role check:', {
        requiredRoles,
        userRole,
        hasRole: !!userRole
    });

    if (!userRole) {
        console.log('[authGuard] 🚫 User has no role assigned');
        showAccessDenied(to, next, 'insufficient_permissions', authRedirect);
        return true;
    }

    // Проверяем наличие роли в списке разрешенных
    const hasAccess = requiredRoles.includes(userRole);

    console.log('[authGuard] 🔍 Role validation:', {
        userRole,
        requiredRoles,
        hasAccess,
        comparison: requiredRoles.map(role => ({
            required: role,
            matches: role === userRole
        }))
    });

    if (!hasAccess) {
        console.log('[authGuard] 🚫 User role not in allowed list');
        showAccessDenied(to, next, 'invalid_role', authRedirect);
        return true;
    }

    // Доступ разрешен
    console.log('[authGuard] ✅ Access granted - all checks passed');
    return false;
}
